import React, { useMemo, useState } from "react";
import { SOCIAL_LINKS } from "../lib/constants";
import { isEmail, isPhone, required } from "../lib/validators";
import { track } from "../lib/analytics";
import ContactThanksModal from "./ContactThanksModal";

/*
function WhatsAppUrl() {
  const digits = WHATSAPP_NUMBER_E164.replace(/[^\d]/g, "");
  const text = encodeURIComponent(WHATSAPP_DEFAULT_TEXT);
  return `https://wa.me/${digits}?text=${text}`;
} */

function SocialIcon({ kind }) {
  if (kind === "instagram") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M7.5 2.8h9A4.7 4.7 0 0 1 21.2 7.5v9A4.7 4.7 0 0 1 16.5 21.2h-9A4.7 4.7 0 0 1 2.8 16.5v-9A4.7 4.7 0 0 1 7.5 2.8Zm9 1.9h-9A2.8 2.8 0 0 0 4.7 7.5v9A2.8 2.8 0 0 0 7.5 19.3h9a2.8 2.8 0 0 0 2.8-2.8v-9a2.8 2.8 0 0 0-2.8-2.8Z"
          fill="currentColor"
        />
        <path
          d="M12 7.6a4.4 4.4 0 1 1 0 8.8 4.4 4.4 0 0 1 0-8.8Zm0 1.9a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
          fill="currentColor"
        />
        <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" />
      </svg>
    );
  }
  if (kind === "facebook") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M13.7 21.2v-7.9h2.6l.4-3.1h-3v-2c0-.9.3-1.5 1.6-1.5h1.6V4c-.3 0-1.4-.1-2.7-.1-2.7 0-4.5 1.6-4.5 4.6v2.6H7.1v3.1h2.6v7.9h3.9Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  if (kind === "x") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M18.7 3.6h2.7l-6 6.8 7.1 9.9h-5.6l-4.4-6.1-5.3 6.1H4.5l6.5-7.4L4.2 3.6H10l4 5.6 4.7-5.6Zm-1 15h1.5L9.2 5.2H7.6l10.1 13.4Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 4h16v16H4V4Zm3.2 3.2h2.7V18H7.2V7.2Zm1.4-1.1a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2ZM11.2 7.2h2.6v1.5h.1c.4-.8 1.5-1.7 3.1-1.7 3.3 0 3.9 2.2 3.9 5V18h-2.7v-4.2c0-1 0-2.4-1.5-2.4s-1.7 1.1-1.7 2.3V18h-2.7V7.2Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function ContactSection() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    hp_field: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);

  const functionUrl = useMemo(() => {
    const base = process.env.REACT_APP_SUPABASE_URL;
    return base ? `${base}/functions/v1/contact-submit` : "";
  }, []);

  function setField(key, val) {
    setForm((p) => ({ ...p, [key]: val }));
  }

  function validate(next) {
    const e = {};
    if (!required(next.full_name)) e.full_name = "Full name is required.";
    if (!required(next.email)) e.email = "Email is required.";
    else if (!isEmail(next.email)) e.email = "Enter a valid email.";
    if (!required(next.phone)) e.phone = "Phone is required.";
    else if (!isPhone(next.phone)) e.phone = "Enter a valid phone number.";
    if (!required(next.subject)) e.subject = "Subject is required.";
    if (!required(next.message) || String(next.message).trim().length < 8)
      e.message = "Message is too short.";
    return e;
  }

  async function submit(e) {
    e.preventDefault();
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) return;

    if (!functionUrl) {
      setErrors((p) => ({
        ...p,
        _form: "Missing Supabase URL. Check your .env file.",
      }));
      return;
    }

    setSubmitting(true);
    track("contact_submit_attempt", { source: "footer_contact" });

    try {
      const res = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || "",
          Authorization: `Bearer ${
            process.env.REACT_APP_SUPABASE_ANON_KEY || ""
          }`,
        },
        body: JSON.stringify({
          ...form,
          email: form.email.trim().toLowerCase(),
          source_page: "footer_contact",
        }),
      });

      let body = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }

      if (!res.ok) {
        const msg =
          (body && (body.error || body.message)) ||
          "Submission failed. Try again.";
        setErrors((p) => ({ ...p, _form: msg }));
        setSubmitting(false);
        return;
      }

      track("contact_submit_success", { source: "footer_contact" });

      setSubmitting(false);
      setThanksOpen(true);
      setForm({
        full_name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        hp_field: "",
      });
      setErrors({});
    } catch {
      setSubmitting(false);
      setErrors((p) => ({ ...p, _form: "Network error. Try again." }));
    }
  }

  return (
    <div className="contactBlock">
      <div className="contactBlock__left">
        <div className="contactTitle">Contact</div>
        <div className="contactSub">Partnerships and producer onboarding.</div>

        <div className="contactActions">
          {/* 
          <a
            className="btn btn--secondary"
            href={WhatsAppUrl()}
            target="_blank"
            rel="noreferrer"
          >
            Reach us on WhatsApp
          </a>
          */}

          <div className="socialRow" aria-label="Social links">
            <a
              className="socialBtn"
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
            >
              <SocialIcon kind="instagram" />
            </a>
            <a
              className="socialBtn"
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
            >
              <SocialIcon kind="facebook" />
            </a>
            <a
              className="socialBtn"
              href={SOCIAL_LINKS.x}
              target="_blank"
              rel="noreferrer"
              aria-label="X"
            >
              <SocialIcon kind="x" />
            </a>
            <a
              className="socialBtn"
              href={SOCIAL_LINKS.linkedin}
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
            >
              <SocialIcon kind="linkedin" />
            </a>
          </div>

          <div className="contactFine">Response target: within 48 hours.</div>
        </div>
      </div>

      <div className="contactBlock__right">
        <form className="contactForm" onSubmit={submit}>
          <div className="contactForm__grid">
            <div className="field">
              <label htmlFor="c_full_name">Full name</label>
              <input
                id="c_full_name"
                value={form.full_name}
                onChange={(e) => setField("full_name", e.target.value)}
                placeholder="Your full name"
                autoComplete="name"
              />
              {errors.full_name ? (
                <div className="field__error">{errors.full_name}</div>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="c_email">Email</label>
              <input
                id="c_email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="you@business.com"
                autoComplete="email"
              />
              {errors.email ? (
                <div className="field__error">{errors.email}</div>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="c_phone">Phone</label>
              <input
                id="c_phone"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                placeholder="+234..."
                autoComplete="tel"
              />
              {errors.phone ? (
                <div className="field__error">{errors.phone}</div>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="c_subject">Subject</label>
              <input
                id="c_subject"
                value={form.subject}
                onChange={(e) => setField("subject", e.target.value)}
                placeholder="Partnership, onboarding, showcase"
              />
              {errors.subject ? (
                <div className="field__error">{errors.subject}</div>
              ) : null}
            </div>
          </div>

          <div className="field">
            <label htmlFor="c_message">Message</label>
            <textarea
              id="c_message"
              rows={4}
              value={form.message}
              onChange={(e) => setField("message", e.target.value)}
              placeholder="Tell us what you need. Share your brand, product category, and goal."
            />
            {errors.message ? (
              <div className="field__error">{errors.message}</div>
            ) : null}
          </div>

          <input
            type="text"
            value={form.hp_field}
            onChange={(e) => setField("hp_field", e.target.value)}
            name="hp_field"
            className="hpField"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          {errors._form ? (
            <div className="contactForm__error">{errors._form}</div>
          ) : null}

          <div className="contactForm__actions">
            <button
              className="btn btn--primary"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Sending..." : "Send message"}
            </button>
          </div>
        </form>
      </div>

      <ContactThanksModal
        isOpen={thanksOpen}
        onClose={() => setThanksOpen(false)}
      />
    </div>
  );
}
